import {useIntl} from '@pinecast/i18n';
import {langForText} from '@pinecast/i18n/language';
import * as React from 'react';

/**
 * What these need of a podcast: the language its text is in. Pass the podcast
 * itself, or any query result for one that has selected `language`.
 */
export type PodcastLanguage = {readonly language: string};

/**
 * A function that gives the `lang` of the text of any podcast: for a list of
 * podcasts. A hook cannot be called once for each row, so this is called once
 * for the list. `usePodcastLang` is this, for one podcast.
 */
export const usePodcastLangOf = (): ((
  podcast: PodcastLanguage | null | undefined,
) => string | undefined) => {
  const {locale} = useIntl();
  return React.useCallback(
    podcast => langForText(podcast?.language, locale),
    [locale],
  );
};

/**
 * The `lang` that the text of `podcast` needs on this page: its language, when
 * that is not the language of the page, and `undefined` when it is (a regional
 * variant counts as the same language).
 *
 * For text that cannot be a node: the value of a field, the text of an
 * `<option>`. Put it on the control. Everything that is a node uses
 * `PodcastString`, which asks the same question.
 */
export const usePodcastLang = (
  podcast: PodcastLanguage | null | undefined,
): string | undefined => usePodcastLangOf()(podcast);

type Props = {
  children?: React.ReactNode;
  /** The podcast whose text this is, or `null` while it loads. */
  podcast: PodcastLanguage | null | undefined;
};

/**
 * Text that the podcaster wrote (a podcast's name, an episode's title, show
 * notes), in the language of their podcast (WCAG 3.1.2, Language of Parts).
 *
 * The dashboard is in the language of its user, and the podcast is often in the
 * same language. Then this renders its children as they are. When it is not, the
 * text is in a span with its own `lang`, and `dir="auto"`: the direction follows
 * the text, which also keeps the words of the page around it from being
 * reordered by it.
 *
 * Takes the whole podcast, and reads what it needs, so that no caller picks the
 * language out of it. A query that does not select `language` does not type
 * check.
 */
const PodcastString = ({children, podcast}: Props) => {
  const lang = usePodcastLang(podcast);
  return lang ? (
    <span dir="auto" lang={lang}>
      {children}
    </span>
  ) : (
    <>{children}</>
  );
};

export default PodcastString;
